import { format } from 'date-fns';
import React from 'react';
import { connect } from 'react-redux';
import { Actions as USAGEACTIONS } from '../../store/actions/usageActions';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { X, ArrowRight } from 'lucide-react';

const UploadHistory = ({ usageUploadHistory, confirmUploadEstimate, showUploadHistory, uploads, importStatusRunning }) => {
  if (!usageUploadHistory) return null;

  return (
    <Dialog open={!!usageUploadHistory} onOpenChange={() => showUploadHistory(false)}>
      <DialogContent
        className='
          bg-background
          p-0
          rounded-lg
          sm:rounded-xl
          w-full
          max-w-2xl
          sm:max-w-4xl
          max-h-[90vh]
          sm:max-h-[90vh]
          flex flex-col
          items-center
          justify-center
          mx-auto
          my-8
          overflow-hidden
        '>
        <Card className='bg-card text-card-foreground border-none shadow-none w-full'>
          <DialogHeader className='bg-header text-header-foreground px-4 sm:px-6 pt-4 sm:pt-6 pb-2'>
            <DialogTitle className='font-default flex items-center justify-between'>
              Upload History
              <Button
                variant='ghost'
                size='sm'
                className='ml-auto text-muted-foreground hover:text-foreground'
                id='close-upload-history'
                onClick={() => showUploadHistory(false)}>
                <X size={16} strokeWidth={1.5} />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <CardContent className='p-4 sm:p-6'>
            <div className='overflow-x-auto hidden sm:block'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='text-nowrap'>File</TableHead>
                    <TableHead className='text-nowrap'>Type</TableHead>
                    <TableHead className='text-nowrap'>Date</TableHead>
                    <TableHead className='text-center text-nowrap'>Unique Routes</TableHead>
                    <TableHead className='text-nowrap'>Estimate</TableHead>
                    <TableHead className='text-nowrap'>Final Cost</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uploads &&
                    uploads.map((row, index) => {
                      const fileName = row.key.substring(row.key.lastIndexOf('/') + 1);
                      return (
                        <TableRow key={index} className='border-b border-border hover:bg-muted/30'>
                          <TableCell className='px-3 py-2 text-nowrap max-w-[180px] truncate' title={fileName}>
                            {fileName}
                          </TableCell>
                          <TableCell className='px-3 py-2 text-nowrap'>{row.type}</TableCell>
                          <TableCell className='px-3 py-2 text-nowrap'>
                            {format(new Date(row.createdAt), 'dd/MM/yyyy')}
                          </TableCell>
                          <TableCell className='px-3 py-2 text-center text-nowrap'>{row.uniqueRoutes}</TableCell>
                          <TableCell className='px-3 py-2 text-nowrap'>{(row.uniqueRoutes * 0.004).toFixed(2)}</TableCell>
                          <TableCell className='px-3 py-2 text-nowrap'>
                            {(row.geoLocationAPICalls * 0.005 + row.distanceMatrixAPICalls * 0.004).toFixed(2)}
                          </TableCell>
                          <TableCell className='px-3 py-2 text-nowrap'>
                            <Button
                              className='min-w-[80px] min-h-[32px] rounded-md'
                              id={`proceed-upload-${row.id}`}
                              disabled={importStatusRunning}
                              onClick={() => confirmUploadEstimate(row.id)}>
                              <ArrowRight size={16} strokeWidth={1.5} className='mr-2' />
                              Proceed
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
            <div className='block sm:hidden max-h-[60vh] overflow-y-auto'>
              <div className='space-y-4'>
                {uploads &&
                  uploads.map((row, index) => (
                    <Card key={index} className='bg-muted text-foreground border border-border'>
                      <CardContent className='p-4'>
                        <div className='flex justify-between items-center mb-2'>
                          <span className='font-semibold text-sm text-muted-foreground'>File</span>
                          <span className='font-default break-all max-w-[18rem] truncate'>
                            {row.key.substring(row.key.lastIndexOf('/') + 1)}
                          </span>
                        </div>
                        <div className='flex justify-between items-center mb-2'>
                          <span className='font-default text-sm text-muted-foreground'>Type</span>
                          <span className='font-default'>{row.type}</span>
                        </div>
                        <div className='flex justify-between items-center mb-2'>
                          <span className='font-default text-sm text-muted-foreground'>Date</span>
                          <span className='font-default'>{format(new Date(row.createdAt), 'dd/MM/yyyy')}</span>
                        </div>
                        <div className='flex justify-between items-center mb-2'>
                          <span className='font-default text-sm text-muted-foreground'>Unique Routes</span>
                          <span className='font-default'>{row.uniqueRoutes}</span>
                        </div>
                        <div className='flex justify-between items-center mb-2'>
                          <span className='font-default text-sm text-muted-foreground'>Estimate</span>
                          <span className='font-default'>{(row.uniqueRoutes * 0.004).toFixed(2)}</span>
                        </div>
                        <div className='flex justify-between items-center mb-4'>
                          <span className='font-default text-sm text-muted-foreground'>Final Cost</span>
                          <span className='font-default'>
                            {(row.geoLocationAPICalls * 0.005 + row.distanceMatrixAPICalls * 0.004).toFixed(2)}
                          </span>
                        </div>
                        <Button
                          className='w-full min-h-[36px] rounded-md'
                          id={`proceed-upload-mobile-${row.id}`}
                          disabled={importStatusRunning}
                          onClick={() => confirmUploadEstimate(row.id)}>
                          <ArrowRight size={16} strokeWidth={1.5} className='mr-2' />
                          Proceed
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  return {
    usageUploadType: state.usageUploadType,
    usageUploadHistory: state.usageUploadHistory,
    uploads: state.myUploads,
    importStatusRunning: state.processingImport?.status?.status === 'RUNNING',
  };
};

const mapDispatchToProps = (dispatch) => ({
  confirmUploadEstimate: (id) => dispatch(USAGEACTIONS.confirmUploadEstimate(id)),
  showUploadHistory: (status) => dispatch(USAGEACTIONS.showUploadHistory(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UploadHistory);
